<script setup lang="ts">
import vLang from "@/directives/lang";
import type { GyldendalTranslation } from "@/types/article-formats/gyldendal";

import ArticleTranslationsContentListItem from "./ArticleTranslationsContentListItem.vue";

const props = defineProps<{
  translations: GyldendalTranslation[];
  articleId: number;
  translationGroupId: number;
  meaningId: number;
  subMeaningId: number;
}>();

const getSeparator = (
  translation: GyldendalTranslation,
  index: number,
  field: keyof GyldendalTranslation
) => {
  if (props.translations[index].examples.length > 0 || props.translations.length === index + 1) {
    return "";
  }

  const fields: Record<string, (translation: GyldendalTranslation) => boolean> = {
    metaLanguageDefinition: hasMetaLanguageDefinition,
    targetRelator: hasTargetRelator,
    translationContentList: hasTranslationContentList,
    targetLanguageDefinition: hasTargetLanguageDefinition,
    targetDescription: hasTargetDescription
  };

  const fieldKeys = Object.keys(fields);
  const fieldStartIndex = fieldKeys.indexOf(field) + 1;
  const shouldAddSeparator = fieldKeys
    .slice(fieldStartIndex)
    .every((fieldKey) => !fields[fieldKey](translation));

  if (shouldAddSeparator) {
    return ", ";
  }

  return "";
};

const hasMetaLanguageDefinition = (translation: GyldendalTranslation): boolean =>
  !!translation.metaLanguageDefinition;

const hasTargetRelator = (translation: GyldendalTranslation): boolean =>
  !!translation.targetRelator;

const hasTranslationContentList = (translation: GyldendalTranslation): boolean =>
  translation.translationContentList.length > 0;

const hasTargetLanguageDefinition = (translation: GyldendalTranslation): boolean =>
  !!translation.targetLanguageDefinition;

const hasTargetDescription = (translation: GyldendalTranslation): boolean =>
  !!translation.targetDescription;
</script>

<template>
  <span>
    <span v-for="(translation, translationIndex) in translations" :key="translationIndex">
      <span v-if="hasMetaLanguageDefinition(translation)">
        <span
          v-lang:source
          class="article-content__meta-text"
          v-html="translation.metaLanguageDefinition"
        ></span
        >{{ getSeparator(translation, translationIndex, "metaLanguageDefinition") }}
      </span>

      <span v-if="hasTargetRelator(translation)" v-lang:target>
        <span class="article-content__meta-text">
          (<span v-html="translation.targetRelator"></span>) </span
        >{{ getSeparator(translation, translationIndex, "targetRelator") }}
      </span>

      <span v-if="hasTranslationContentList(translation)">
        <span
          v-for="(content, contentIndex) in translation.translationContentList"
          :key="contentIndex"
        >
          <ArticleTranslationsContentListItem
            :item="content"
            :article-id="articleId"
            :section-id="translationGroupId"
            text-class="article-content__text article-content__text--prominent" /></span
        >{{ getSeparator(translation, translationIndex, "translationContentList") }}
      </span>

      <span v-if="hasTargetLanguageDefinition(translation)">
        <span v-lang:target v-html="translation.targetLanguageDefinition"></span
        >{{ getSeparator(translation, translationIndex, "targetLanguageDefinition") }}
      </span>

      <span v-if="hasTargetDescription(translation)" v-lang:target>
        <span class="article-content__meta-text">
          (<span v-html="translation.targetDescription"></span>) </span
        >{{ getSeparator(translation, translationIndex, "targetDescription") }}
      </span>

      <ul
        v-for="(example, exampleIndex) in translation.examples"
        :key="exampleIndex"
        v-lang:target
        class="article-content__example-list"
      >
        <li class="article-content__example-list-item article-content__example-list-item--square">
          <i class="article-content__example-text" v-html="example"></i>
        </li>
      </ul>
    </span>
  </span>
</template>
